import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ModernPageHeader from "@/components/layout/ModernPageHeader";

interface SuperAdminLayoutProps {
  children: React.ReactNode;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  tabs?: Array<{
    id: string;
    label: string;
    icon?: React.ReactNode;
    badge?: number;
  }>;
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  fullWidth?: boolean;
}

export const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({
  children,
  title,
  description,
  actions,
  tabs,
  activeTab,
  onTabChange,
  fullWidth,
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, isAdminLoading } = useAuth();

  React.useEffect(() => {
    // If admin loading is complete and user is not admin, redirect
    if (!isAdminLoading && !isAdmin) {
      toast({
        title: "Access denied",
        description: "You don't have permission to access the Super Admin area",
        variant: "destructive",
      });
      navigate("/");
    }
  }, [isAdmin, isAdminLoading, navigate, toast]);

  // Show loading while checking admin status
  if (isAdminLoading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Don't render anything if not admin (will redirect from useEffect)
  if (!isAdmin) {
    return null;
  }

  // Add the Shield icon to the title
  const headerTitle = (
    <div className="flex items-center">
      <Shield className="h-5 w-5 text-primary mr-2" />
      {title}
    </div>
  );

  return (
    <div
      className={`${fullWidth ? "w-full" : "container mx-auto max-w-7xl"} bg-white h-full flex flex-col rounded-lg shadow-sm`}
    >
      <ModernPageHeader
        title={headerTitle as any}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        actions={actions}
        hideBottomBorder={true}
      />

      {description && (
        <div className="px-8 pt-2 pb-4">
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      )}

      <div className="px-8 pb-8 flex-1 overflow-auto">{children}</div>
    </div>
  );
};
